import {inject, Injectable} from '@angular/core';
import {HrTool} from '@frontend/directus';
import {roundToDecimals} from '@frontend/shared';
import {
  EvaluationCriteriaResponse,
  ProjectSettingResponse,
  ProviderTenderInvitationOfferOverviewResponse,
} from '@frontend/shared-api';
import {
  CriteriaRow,
  ProviderColumn,
  ProviderResult,
  SubCriteriaRow,
} from '../provider-results-table.types';
import {RfqCriteriaTableData} from './rfq-provider-results.types';
import {TotalCostCalculationService} from '../../../../services/total-cost-calculation.service';
import {RFQ_FEE_SC_NUMBERS} from '../../../../services/rfq-fee-category.service';

@Injectable({
  providedIn: 'root',
})
export class RfqProviderResultsService {
  private static readonly RFQ_SUBCRITERIA_NUMBERS: number[] = Object.values(RFQ_FEE_SC_NUMBERS);

  private readonly totalCostService = inject(TotalCostCalculationService);

  buildProviderColumns(
    overviews: ProviderTenderInvitationOfferOverviewResponse[],
    hrTools: HrTool[],
  ): ProviderColumn[] {
    const toolsById = new Map(hrTools.map((tool) => [tool.id.toString(), tool]));
    return overviews.map((overview) => {
      const providerId = overview.providerTenantMappingResponse.providerTenantId;
      const hrToolDirectusId = overview.providerTenantMappingResponse.hrToolDirectusId;
      const providerName = overview.providerTenantMappingResponse.originalName;
      const hrTool =
        toolsById.get(hrToolDirectusId) ??
        this.createPlaceholderHrTool(hrToolDirectusId, providerName);

      return {
        hrToolDirectusId,
        providerTenantId: providerId,
        hrTool,
        translatedName: providerName,
      };
    });
  }

  calculateSubCriteriaScore(
    providerId: string,
    scNumber: number,
    providerIds: string[],
    overviews: ProviderTenderInvitationOfferOverviewResponse[],
    projectSettings: ProjectSettingResponse | undefined,
    isExponentialScale: boolean,
  ): {value: number; maxValue: number} | undefined {
    const cost = this.getCost(providerId, scNumber, overviews);
    if (cost === undefined) {
      return undefined;
    }

    const minCost = this.getMinCostForSubCriteria(scNumber, providerIds, overviews);
    if (minCost === undefined) {
      return undefined;
    }

    const maxScore = this.getMaxScore(isExponentialScale);
    return {
      value: this.calculateScore(cost, minCost, projectSettings, isExponentialScale),
      maxValue: maxScore,
    };
  }

  calculateRfqCriteriaProviderResult(
    providerId: string,
    subCriteriaRows: SubCriteriaRow[],
  ): {value: number; maxValue: number} | undefined {
    const values = subCriteriaRows
      .map((subCriteria) => ({
        result: subCriteria.providerResultsByHrToolId.get(providerId),
        weight: subCriteria.weight ?? 1.0,
        maxValue: subCriteria.maxValue,
      }))
      .filter((item) => item.result !== undefined);

    if (values.length !== subCriteriaRows.length || values.length === 0) {
      return undefined;
    }

    return {
      value: values.reduce((sum, item) => sum + (item.result?.value ?? 0) * item.weight, 0),
      maxValue: values.reduce((sum, item) => sum + roundToDecimals(item.maxValue * item.weight), 0),
    };
  }

  buildRfqTableData(
    criteriaData: RfqCriteriaTableData,
    providers: ProviderColumn[],
    overviews: ProviderTenderInvitationOfferOverviewResponse[],
    projectSettings: ProjectSettingResponse | undefined,
    isExponentialScale: boolean,
    createProviderResult: (value: number, maxValue: number) => ProviderResult,
  ): CriteriaRow[] {
    const providerIds = providers.map(
      (provider) => provider.providerTenantId ?? provider.hrToolDirectusId,
    );

    const subCriteriaRows: SubCriteriaRow[] = criteriaData.includedSubCriteria.map(
      (subCriteria) => {
        const providerDataByHrToolId = new Map<string, ProviderResult>();
        const maxValue = this.getMaxScore(isExponentialScale);

        providers.forEach((provider) => {
          const providerTenantId = provider.providerTenantId ?? provider.hrToolDirectusId;
          const score = this.calculateSubCriteriaScore(
            providerTenantId,
            subCriteria.scNumber,
            providerIds,
            overviews,
            projectSettings,
            isExponentialScale,
          );
          if (!score) {
            return;
          }
          providerDataByHrToolId.set(
            provider.hrToolDirectusId,
            createProviderResult(score.value, score.maxValue),
          );
        });

        return {
          scNumber: subCriteria.scNumber,
          directusId: subCriteria.directusId,
          translatedLabel: subCriteria.translatedLabel,
          maxValue,
          weight: subCriteria.weight,
          providerResultsByHrToolId: providerDataByHrToolId,
        };
      },
    );

    const providerData = new Map<string, ProviderResult>();
    providers.forEach((provider) => {
      const score = this.calculateRfqCriteriaProviderResult(
        provider.hrToolDirectusId,
        subCriteriaRows,
      );
      if (!score) {
        return;
      }
      providerData.set(
        provider.hrToolDirectusId,
        createProviderResult(score.value, score.maxValue),
      );
    });

    return [
      {
        ecNumber: criteriaData.ecNumber,
        translatedLabel: criteriaData.translatedLabel,
        maxValue: this.getCriteriaMaxValue(subCriteriaRows, isExponentialScale),
        weight: criteriaData.weight,
        directusId: criteriaData.directusId,
        subCriteria: subCriteriaRows,
        providerResultsByHrToolId: providerData,
        expanded: false,
      },
    ];
  }

  calculateRfqTotalPercentages(
    ec9: EvaluationCriteriaResponse | undefined,
    overviews: ProviderTenderInvitationOfferOverviewResponse[],
    projectSettings: ProjectSettingResponse | undefined,
    isExponentialScale: boolean,
  ): Map<string, number> {
    const providerIds = overviews.map((o) => o.providerTenantMappingResponse.providerTenantId);
    const includedSubCriteria = (ec9?.subCriteria ?? []).filter(
      (sc) =>
        sc.included === true &&
        RfqProviderResultsService.RFQ_SUBCRITERIA_NUMBERS.includes(sc.scNumber),
    );

    const percentages = new Map<string, number>();

    providerIds.forEach((providerId) => {
      let totalScore = 0;
      let totalMax = 0;

      includedSubCriteria.forEach((sc) => {
        const score = this.calculateSubCriteriaScore(
          providerId,
          sc.scNumber,
          providerIds,
          overviews,
          projectSettings,
          isExponentialScale,
        );
        if (!score) return;

        const weight = sc.weight ?? 1.0;
        totalScore += score.value * weight;
        totalMax += roundToDecimals(score.maxValue * weight);
      });

      const percentage = totalMax > 0 ? (totalScore / totalMax) * 100 : 0;
      percentages.set(providerId, percentage);
    });

    return percentages;
  }

  getCriteriaMaxValue(subCriteriaRows: SubCriteriaRow[], isExponentialScale: boolean): number {
    const weightedMax = subCriteriaRows.reduce((sum, subCriteria) => {
      const weight = subCriteria.weight ?? 1.0;
      return sum + roundToDecimals(subCriteria.maxValue * weight);
    }, 0);
    return this.snapNearMaxValue(weightedMax, isExponentialScale);
  }

  getMaxScore(isExponentialScale: boolean): number {
    return isExponentialScale ? 16 : 4;
  }

  private getMinCostForSubCriteria(
    scNumber: number,
    providerIds: string[],
    overviews: ProviderTenderInvitationOfferOverviewResponse[],
  ): number | undefined {
    const costs = providerIds
      .map((providerId) => this.getCost(providerId, scNumber, overviews))
      .filter((value): value is number => value !== undefined);
    if (costs.length === 0) {
      return undefined;
    }

    return Math.min(...costs);
  }

  private calculateScore(
    cost: number,
    minCost: number,
    projectSettings: ProjectSettingResponse | undefined,
    isExponentialScale: boolean,
  ): number {
    const maxScore = this.getMaxScore(isExponentialScale);
    const minPriceOffer = projectSettings?.minPriceOffer ?? 1;
    const maxPriceToGetPoints = projectSettings?.maxPriceToGetPoints ?? 1;
    const lowerBound = minCost * minPriceOffer;
    const upperBound = minCost * maxPriceToGetPoints;

    if (cost <= lowerBound) {
      return maxScore;
    }
    if (upperBound <= lowerBound) {
      return 0;
    }
    if (cost >= upperBound) {
      return 0;
    }

    const linearRatio = (upperBound - cost) / (upperBound - lowerBound);
    return roundToDecimals(maxScore * linearRatio);
  }

  private getCost(
    providerId: string,
    scNumber: number,
    overviews: ProviderTenderInvitationOfferOverviewResponse[],
  ): number | undefined {
    const overview = overviews.find(
      (item) => item.providerTenantMappingResponse.providerTenantId === providerId,
    )?.invitationOfferOverviewResponse;
    if (!overview) {
      return undefined;
    }

    switch (scNumber) {
      case RFQ_FEE_SC_NUMBERS.service:
        return this.totalCostService.calculateServiceFeesTotal(overview);
      case RFQ_FEE_SC_NUMBERS.implementation:
        return this.totalCostService.calculateImplementationFeesTotal(overview);
      case RFQ_FEE_SC_NUMBERS.integrationSetup:
        return this.totalCostService.calculateIntegrationSetupFeesTotal(overview);
      case RFQ_FEE_SC_NUMBERS.other:
        return this.totalCostService.calculateIntegrationOtherFeesTotal(overview);
      default:
        return undefined;
    }
  }

  private snapNearMaxValue(value: number, isExponentialScale: boolean): number {
    const maxScore = this.getMaxScore(isExponentialScale);
    const rounded = roundToDecimals(value);
    return Math.abs(rounded - maxScore) <= 0.05 ? maxScore : rounded;
  }

  private createPlaceholderHrTool(providerId: string, providerName: string): HrTool {
    return {
      id: providerId,
      name: providerName,
      toolProvider: {id: providerId, name: providerName},
      coverImage: '',
      hostingProviderLocation: '',
      website: null,
      images: [],
      serviceModels: [],
      availableCountries: [],
      availableLanguages: [],
      moduleFunctions: [],
      translations: [],
      minEmployeeCount: null,
      maxEmployeeCount: null,
      certificationDirectusUuids: [],
    };
  }
}
